// https://github.com/korywka/crypto-aes-gcm/blob/master/index.js
export const encrypt = async (
  crypto: Crypto,
  plaintext: string,
  password: string
): Promise<string> => {
  const pwUtf8 = new TextEncoder().encode(password);
  const pwHash = await crypto.subtle.digest('SHA-256', pwUtf8);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ivStr = Array.from(iv)
    .map((b) => String.fromCharCode(b))
    .join('');
  const alg = { name: 'AES-GCM', iv: iv };
  const key = await crypto.subtle.importKey('raw', pwHash, alg, false, ['encrypt']);
  const ptUint8 = new TextEncoder().encode(plaintext);
  const ctBuffer = await crypto.subtle.encrypt(alg, key, ptUint8);
  const ctArray = Array.from(new Uint8Array(ctBuffer));
  const ctStr = ctArray.map((byte) => String.fromCharCode(byte)).join('');
  return btoa(ivStr + ctStr);
};

const encryptWithForge = async (data: string, secret: string): Promise<string> => {
  const forge = await import('node-forge');
  const md = forge.md.sha256.create();
  md.update(secret, 'utf8');
  const key = md.digest().data;
  const iv = forge.random.getBytesSync(12);
  const ivStr = iv.toString();
  const cipher = forge.cipher.createCipher('AES-GCM', key);
  cipher.start({
    iv: iv,
  });
  cipher.update(forge.util.createBuffer(data));
  cipher.finish();
  const encrypted = cipher.output.data;
  const tag = cipher.mode.tag.data;
  return btoa(ivStr + encrypted + tag);
};

export const decrypt = async (
  crypto: Crypto,
  cipherText: string,
  secret: string
): Promise<string> => {
  const pwUtf8 = new TextEncoder().encode(secret);
  const pwHash = await crypto.subtle.digest('SHA-256', pwUtf8);
  const ivStr = atob(cipherText).slice(0, 12);
  const iv = new Uint8Array(Array.from(ivStr).map((ch) => ch.charCodeAt(0)));
  const alg = { name: 'AES-GCM', iv: iv };
  const key = await crypto.subtle.importKey('raw', pwHash, alg, false, ['decrypt']);
  const ctStr = atob(cipherText).slice(12);
  const ctUint8 = new Uint8Array(Array.from(ctStr).map((ch) => ch.charCodeAt(0)));
  try {
    const plainBuffer = await crypto.subtle.decrypt(alg, key, ctUint8);
    return new TextDecoder().decode(plainBuffer);
  } catch (error) {
    throw new Error('decrypt failed');
  }
};

const decryptWithForge = async (cipherText: string, password: string): Promise<string> => {
  const forge = await import('node-forge');
  const md = forge.md.sha256.create();
  md.update(password, 'utf8');
  const key = md.digest().data;

  const ivStr = atob(cipherText).slice(0, 12);
  const ctStr = atob(cipherText).slice(12);

  const decipher = forge.cipher.createDecipher('AES-GCM', key);
  decipher.start({
    iv: forge.util.createBuffer(ivStr),
    tag: forge.util.createBuffer(ctStr.slice(ctStr.length - 16)),
  });
  decipher.update(forge.util.createBuffer(ctStr.slice(0, ctStr.length - 16)));
  const pass = decipher.finish();
  if (!pass) {
    throw new Error('decrypt failed');
  }
  return decipher.output.data;
};

export const packData = async (data: Record<string, string>, key: string): Promise<string> => {
  let encrypted: string;
  if (typeof crypto.subtle === 'undefined') {
    encrypted = await encryptWithForge(JSON.stringify(data), key);
  } else {
    encrypted = await encrypt(window.crypto, JSON.stringify(data), key);
  }
  return window.btoa(encodeURIComponent(encrypted));
};

export const unpackData = async (
  str: string,
  password: string
): Promise<Record<string, string>> => {
  const cipherText = decodeURIComponent(window.atob(str));
  let decrypted: string;
  if (typeof crypto.subtle === 'undefined') {
    decrypted = await decryptWithForge(cipherText, password);
  } else {
    decrypted = await decrypt(window.crypto, cipherText, password);
  }
  return JSON.parse(decrypted);
};
